import { VARIABLE_TYPES } from '../data/Constants';
import { parse, isValid, isBefore, differenceInYears } from 'date-fns';

/**
 * Validates a date of birth string in MM/DD/YYYY format.
 * @param {string} dobString - The date of birth string (MM/DD/YYYY)
 * @returns {string} - Error message if invalid, empty string if valid
 */
export function validateDOB(dobString) {
  const dob = parse(dobString, 'MM/dd/yyyy', new Date());
  const today = new Date();
  const maxAge = 120;

  if (!isValid(dob)) {
    return 'Please enter a valid date of birth.';
  }
  if (!isBefore(dob, today)) {
    return 'Date of birth must be in the past.';
  }
  if (differenceInYears(today, dob) > maxAge) {
    return `Date of birth cannot be more than ${maxAge} years ago.`;
  }
  return '';
}

export const isValidEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const isValidDate = (date) => {
  if (date.length < 10) return false;

  try {
    new Date(date).toISOString();
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Check a string or number to see if it's a valid zip code.
 * @param {number|string} zip
 * @returns {boolean}
 */
export const isValidZipCode = (zip) => {
  // Some zip codes begin with a 0, so we need to loop through
  // all of the characters to determine if they are, individually,
  // numbers.
  for (let i = 0; i < zip.length; i++) {
    const checkChar = Number(zip[i]);
    if (isNaN(checkChar) || !validateData(checkChar, 'number', false))
      return false;
  }

  // Make sure the zip length is only 5
  if (zip.length !== 5) return false;

  // Else, return true
  return true;
};

/**
 * Use for type checking
 * @param {*} data - the data to validate.
 * @param {(string|string[])} type - can be a string or an array of strings.
 * @param {boolean} [angryFail=true] - True, fail with an error. False, fail by returning true/false.
 * @returns {boolean} true/false only if angryFail is set to false.
 */
export const validateData = (data, type, angryFail = true, loopDepth = 0) => {
  // make sure the looping doesn't go nuts somehow.
  if (loopDepth >= 50) {
    throw Error('Recursive loop counter maxed out at 50');
    return;
  }

  // Set the next loop depth
  const nextLoopDepth = loopDepth + 1;

  // if the type parameter is a string
  if (typeof type === 'string') {
    const useType = type.toLowerCase();

    if (VARIABLE_TYPES.indexOf(useType) === -1) {
      if (angryFail)
        throw Error(
          `TypeError: '${useType}' is not an expected type, ex: (${VARIABLE_TYPES.join(
            ',',
          )})`,
        );
      else return false;
    }

    let failedChecks = false;

    // If zip code (stop processing other rules)
    if (useType === 'zipcode') {
      if (!isValidZipCode(data)) failedChecks = true;
    }

    // Check all remaining available types
    else if (
      (useType === 'array' && !Array.isArray(data)) || // Special check to see if data is an array
      (useType === 'object' &&
        typeof data === 'object' && // object data check
        (Array.isArray(data) || data === '' || data === null)) || // verify that the data is an object and not an array or not null
      typeof data !== useType // general type check
    )
      failedChecks = true;

    // Check to see if we passed all of the checks.
    if (failedChecks) {
      if (angryFail)
        throw Error(`TypeError: '${data}' must be of type '${useType}'`);
      else return false;
    }
  }

  // else, check type assuming that it's an array
  else {
    let loopSuccess = false;

    // Verify that the type parameter is an array
    if (!Array.isArray(type)) {
      if (angryFail)
        throw Error(`TypeError: type must be of type string or array.`);
      else return false;
    }

    // Validate the array of types.  This is an "or" check so if one of them
    // triggers to be true then automagically return true.
    for (const t of type) {
      if (validateData(data, t, false, nextLoopDepth)) {
        if (angryFail) {
          loopSuccess = true;
          break;
        } else return true;
      }
    }

    // If we are here, it's either because angryFail is set and the loop failed to find
    // the type... or angryFail is false and we just need to exit with false because no
    // types were found.
    if (angryFail && !loopSuccess)
      throw Error(
        `TypeError: '${data}' must be of type '${type.join(', or ')}'`,
      );
    else if (!angryFail) return false;
  }

  if (!angryFail) return true;
};
